import { useEffect, useRef, useState, useMemo } from 'react';
import Select from 'react-select';
import Switch from 'react-switch';
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api';
import { ReactSVG } from 'react-svg';
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';

import { env } from '../../config';
import { formatDate, isAfAdmin } from '../../utils';
import { getBasePlaylists, savePlaylist } from '../Screen/utils';
import PreviewModal from '../Screen/PreviewModal';
import { getSubLocationsByLocationId, getSublocationsByPlaylistId, createSublocation, deleteSublocation } from './utils';
import BrandingConfiguration from './BrandingConfiguration';
import JourneyPlaylistConfiguration from './JourneyPlaylistConfiguration';
import JourneyInsight from './JourneyInsight';
import SublocationEditModal from './SublocationEditModal';
import AddressField from '../AddressField';
import { getLocations } from '../Locations/utils';
import dropdownOptions from '../Screen/dropdownOptions.json';
import journeyPlaylistFeatures from './journeyPlaylistFeatures.json';

import plusIcon from '../../images/icons/screens/white-plus.svg';
import copyIcon from '../../images/icons/screens/copy.svg';
import gearIcon from '../../images/icons/screens/gear.svg';
import paintbrushIcon from '../../images/icons/screens/paintbrush.svg';
import playlistIcon from '../../images/icons/screens/playlist.svg';
import mapMarkerIcon from '../../images/icons/screens/map-marker.svg';
import openReportIcon from '../../images/icons/analytics/open-report.svg';
import eyeIcon from '../../images/icons/screens/eye.svg';
import returnIcon from '../../images/icons/screens/return.svg';
import phoneTouchIcon from '../../images/icons/screens/interactivity.svg';
import chartUpwardIcon from '../../images/icons/chart-upward.svg';
import insightIcon from '../../images/icons/insight.svg';
import saveIcon from '../../images/icons/screens/save.svg';
import flagIcon from '../../images/icons/flag.svg';
import editIcon from '../../images/icons/edit.svg';
import trashIcon from '../../images/icons/foresight/trash.svg';
import dragHandleIcon from '../../images/icons/journey/drag-handle.svg';
import checkIcon from '../../images/icons/check.svg';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Default sort when no manual order has been saved yet — final destinations first, then by
// creation date. Once the user drags a hub, the saved order (see sortHubsByOrder) takes over
// completely, finals included, so a hub dragged in front of the destination stays there.
const sortHubsDefault = (subs) => [...subs].sort((a, b) => {
    if (a.isFinal !== b.isFinal) return a.isFinal ? -1 : 1;
    return new Date(a.createdAt) - new Date(b.createdAt);
});

const arrayMove = (arr, fromIndex, toIndex) => {
    const copy = arr.slice();
    const [item] = copy.splice(fromIndex, 1);
    copy.splice(toIndex, 0, item);
    return copy;
};

// The saved hub order lives as playlist.journeyConfigs.hubOrder — just a plain list of
// sublocation ids, kept separate from journeyConfigs.sublocations (which stores each hub's
// name/isActive override and has nothing to do with order). A hub with no entry there yet
// (created after the order was last saved) falls back to the default sort and is appended after
// every hub that does have a saved position.
const sortHubsByOrder = (apiSubs, hubOrder) => {
    if (!Array.isArray(hubOrder) || hubOrder.length === 0) {
        return sortHubsDefault(apiSubs);
    }
    const orderIndex = new Map(hubOrder.map((id, i) => [id, i]));
    const known = apiSubs
        .filter(s => orderIndex.has(s.id))
        .sort((a, b) => orderIndex.get(a.id) - orderIndex.get(b.id));
    const unknown = sortHubsDefault(apiSubs.filter(s => !orderIndex.has(s.id)));
    return [...known, ...unknown];
};

const CLASSIFICATION_OPTIONS = [
    { value: 'airports', label: 'Airports' },
    { value: 'conferencesAndEvents', label: 'Conferences and Events' },
    { value: 'hospitality', label: 'Hospitality' },
    { value: 'multifamilyResidential', label: 'Multifamily Residential' },
    { value: 'retail', label: 'Retail' },
    { value: 'sportsAndEntertainment', label: 'Sports and Entertainment' },
    { value: 'transportationAgencies', label: 'Transportation Agencies' },
    { value: 'tma', label: 'TMA' },
    { value: 'universitiesAndCampuses', label: 'Universities and Campuses' },
    { value: 'workplacesAndHumanResources', label: 'Workplaces and Human Resources' },
];

const selectStyles = {
    container: (base, state) => ({ ...base, flex: 1 }),
    control: (base, state) => ({
        ...base,
        border: 'none',
        boxShadow: state.isDisabled ? 'unset' : '0px 2px 8px 4px rgba(0,0,0,0.09)',
        borderRadius: '8px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    option: (base, state) => ({
        ...base,
        cursor: 'pointer',
        color: state.isDisabled ? '#919191' : 'inherit',
    }),
};

const icons = {
    eyeIcon,
    gearIcon,
    insightIcon,
    paintbrushIcon,
    phoneTouchIcon,
    playlistIcon,
    returnIcon,
    saveIcon
};

const MAPS_LIBRARIES = ['places'];

const JourneyPlaylistPage = ({ userData, customer, handleContentModalOpen, permissions }) => {
    const [playlist, setPlaylist] = useState(null);
    const [savedPlaylist, setSavedPlaylist] = useState(null);
    const [sublocations, setSublocations] = useState([]);
    const [locations, setLocations] = useState([]);
    const [stateChanged, setStateChanged] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ message: null, error: false });
    const [journeyPlaylists, setJourneyPlaylists] = useState([]);
    const [showNewPlaylistModal, setShowNewPlaylistModal] = useState(false);
    const [showOpenPlaylistModal, setShowOpenPlaylistModal] = useState(false);
    const [showUndoConfirmModal, setShowUndoConfirmModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState('settings');
    const [editingSublocation, setEditingSublocation] = useState(null);
    const [showCreateHubModal, setShowCreateHubModal] = useState(false);
    // Non-null once the delete confirm modal is open — { hub, affectedPlaylists }.
    const [deletingHub, setDeletingHub] = useState(null);
    // Id of the hub whose affected playlists are being looked up, before its modal opens.
    const [checkingHubId, setCheckingHubId] = useState(null);
    // Non-null while a divider is being created or edited — { id, label }. A brand new divider's
    // id doesn't exist in journeyConfigs.dividers yet (nothing is written until confirmed); an
    // existing one's does, and confirming just overwrites that entry in place.
    const [draftDivider, setDraftDivider] = useState(null);
    const openedLocationIdRef = useRef(null);

    // A small activation distance keeps a plain click on the drag handle from being swallowed
    // as a zero-length drag.
    const hubSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    // The hub being dragged, the item it's currently over, and every row's height measured when
    // the drag started (see hubDragShifts below). Null when nothing is being dragged.
    const [hubDrag, setHubDrag] = useState(null);
    const hubsTableRef = useRef(null);

    const { isLoaded: mapLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: env.GMAPS_KEY || '',
        libraries: MAPS_LIBRARIES,
    });

    const getBasePlaylistsWrapper = () => {
        return getBasePlaylists(
            customer.id,
            customer.id === userData.customer_id && userData.role !== 'af_admin' ? userData.sub : null,
            'journey'
        ).then(data => {
            setJourneyPlaylists(data);
            return data;
        });
    }

    useEffect(() => {
        getBasePlaylistsWrapper();

        getLocations(customer.id).then(data => {
            if (Array.isArray(data)) {
                let sortedLocations = data.sort((a, b) => a.name.localeCompare(b.name));
                setLocations(sortedLocations);
            }
        });
    }, []);

    useEffect(() => {
        const openedFor = openedLocationIdRef.current;
        openedLocationIdRef.current = null;

        if (!playlist?.locationId) {
            setSublocations([]);
            return;
        }
        // Skip: getSublocationsByPlaylistId already fetched sublocations for this location on Open.
        if (openedFor === playlist.locationId) return;

        getSubLocationsByLocationId(playlist.locationId).then(apiSubs => {
            if (!Array.isArray(apiSubs)) return;
            // Display order is derived from journeyConfigs.hubOrder at render time (see
            // subsWithBadge) — no need to sort before storing here.
            setSublocations(apiSubs);

            const freshSubs = apiSubs.map(s => ({ sublocationId: s.id, name: s.name, isActive: true }));
            setPlaylist(prev => {
                const existingSubs = prev?.journeyConfigs?.sublocations || [];
                const freshSubs = apiSubs.map(s => {
                    const existing = existingSubs.find(c => c.sublocationId === s.id);
                    return {
                        sublocationId: s.id,
                        name: s.name,
                        isActive: existing?.isActive ?? true,
                    };
                });
                const journeyConfigs = { ...(prev?.journeyConfigs || {}) };
                if (freshSubs.length > 0) {
                    journeyConfigs.sublocations = freshSubs;
                } else {
                    delete journeyConfigs.sublocations;
                }
                return { ...prev, journeyConfigs };
            });
        });
    }, [playlist?.locationId]);

    // Merge API sublocation data (lat/lng, isFinal) with journeyConfigs (name, isActive). Hub
    // order is a separate journeyConfigs.hubOrder list (see sortHubsByOrder) rather than stored
    // locally, so Undo/Save/Revert all just work by virtue of already operating on
    // playlist.journeyConfigs.
    const configSubs = playlist?.journeyConfigs?.sublocations || [];
    const hubOrder = playlist?.journeyConfigs?.hubOrder || [];
    let letterIdx = 0;
    const subsWithBadge = sortHubsByOrder(sublocations, hubOrder)
        .map(apiSub => {
            const cfg = configSubs.find(s => s.sublocationId === apiSub.id);
            const nameRule = (apiSub.rules || []).find(r =>
                r.type === 'name' && r.isActive && r.sublocationId === apiSub.id
            );
            const activeRule = (apiSub.rules || []).find(r =>
                r.type === 'isActive' && r.sublocationId === apiSub.id
            );
            return {
                ...apiSub,
                name: nameRule?.config?.value || apiSub.name,
                isActive: activeRule ? !!activeRule.config?.value : (cfg?.isActive ?? true),
                badge: apiSub.isFinal ? 'F' : LETTERS[letterIdx++],
            };
        });

    // Dividers are playlist-local — they have no sublocation record of their own, just an entry
    // in journeyConfigs.dividers (id -> label) and a slot in the same journeyConfigs.hubOrder list
    // hubs use. Weaving them into subsWithBadge's already-ordered sequence here, rather than
    // letting sortHubsByOrder know about them, keeps letter-badge assignment (above) purely a
    // hub concern — dividers never consume a letter.
    const dividers = playlist?.journeyConfigs?.dividers || {};
    const seenItemIds = new Set();
    const hubItemsById = new Map(subsWithBadge.map(hub => [hub.id, hub]));
    const journeyHubItems = [];
    hubOrder.forEach(id => {
        if (hubItemsById.has(id)) {
            journeyHubItems.push({ kind: 'hub', id, hub: hubItemsById.get(id) });
            seenItemIds.add(id);
        } else if (dividers[id]) {
            journeyHubItems.push({ kind: 'divider', id, divider: dividers[id] });
            seenItemIds.add(id);
        }
    });
    subsWithBadge.filter(hub => !seenItemIds.has(hub.id)).forEach(hub => {
        journeyHubItems.push({ kind: 'hub', id: hub.id, hub });
    });

    // While a row is dragged, its own slot (drawn as a placeholder) moves to where the drop would
    // land and the rows in between slide over by its height, so the gap always shows the result
    // of letting go — the same positions arrayMove produces in handleHubDragEnd. Heights come from
    // offsetHeight, which ignores these transforms, and dnd-kit measures drop targets without
    // transforms too, so the shifting rows never change what counts as "over".
    const hubDragShifts = {};
    if (hubDrag) {
        const from = journeyHubItems.findIndex(item => item.id === hubDrag.activeId);
        const to = journeyHubItems.findIndex(item => item.id === hubDrag.overId);
        if (from !== -1 && to !== -1 && from !== to) {
            const step = from < to ? 1 : -1;
            const heightOf = id => hubDrag.heights[id] || 0;
            let activeShift = 0;
            for (let i = from + step; i !== to + step; i += step) {
                const { id } = journeyHubItems[i];
                hubDragShifts[id] = -step * heightOf(hubDrag.activeId);
                activeShift += step * heightOf(id);
            }
            hubDragShifts[hubDrag.activeId] = activeShift;
        }
    }
    const draggedHubItem = hubDrag && journeyHubItems.find(item => item.id === hubDrag.activeId);

    const DC = { lat: 38.9072, lng: -77.0369 };
    const selectedLocation = locations.find(l => l.id === playlist?.locationId);
    const mappable = sublocations.filter(s => isFinite(parseFloat(s.latitude)) && isFinite(parseFloat(s.longitude)));
    const mapCenter = (selectedLocation && isFinite(parseFloat(selectedLocation.latitude)) && isFinite(parseFloat(selectedLocation.longitude)))
        ? { lat: parseFloat(selectedLocation.latitude), lng: parseFloat(selectedLocation.longitude) }
        : mappable.length > 0
            ? {
                lat: mappable.reduce((s, sub) => s + parseFloat(sub.latitude), 0) / mappable.length,
                lng: mappable.reduce((s, sub) => s + parseFloat(sub.longitude), 0) / mappable.length,
            }
            : DC;

    const handlePlaylistChange = (field, value) => {
        setPlaylist(prev => ({ ...prev, [field]: value }));
        setStateChanged(true);
    };

    const handleBrandingChange = (field, value) => {
        setPlaylist(prev => ({
            ...prev,
            brandingConfigs: { ...(prev?.brandingConfigs || {}), [field]: value }
        }));
        setStateChanged(true);
    };

    // name and isFinal both live on the sublocation record itself (already persisted via
    // updateSublocation), not in journeyConfigs, so they update the sublocations list directly
    // rather than going through the playlist-level Save.
    const handleSublocationSave = ({ sublocationId, name, isFinal }) => {
        setSublocations(prev => prev.map(s => s.id === sublocationId ? { ...s, name, isFinal } : s));
    };

    const handleRulesSave = (sublocationId, rules) => {
        setSublocations(prev => prev.map(s => s.id === sublocationId ? { ...s, rules } : s));
    };

    const handleHubCreated = (newSublocation) => {
        setSublocations(prev => [...prev, newSublocation]);
    };

    // Hub deletion is restricted to ActionFigure employees — it removes the sublocation record
    // itself, so it affects every playlist at this location, not just this one.
    const canDeleteHubs = userData?.role === 'af_admin';

    // The sublocation is deleted immediately (not staged for Save). Its id is also stripped from
    // journeyConfigs on both the working and saved copies so neither Save nor Undo brings back a
    // dangling reference.
    const handleDeleteHub = async (hubId) => {
        try {
            await deleteSublocation(hubId);
        } catch (e) {
            setStatusMessage({ message: 'Something went wrong deleting this hub. Please try again.', error: true });
            return;
        }
        const stripHub = (pl) => {
            if (!pl?.journeyConfigs) return pl;
            const journeyConfigs = { ...pl.journeyConfigs };
            if (journeyConfigs.sublocations) {
                journeyConfigs.sublocations = journeyConfigs.sublocations.filter(s => s.sublocationId !== hubId);
            }
            if (journeyConfigs.hubOrder) {
                journeyConfigs.hubOrder = journeyConfigs.hubOrder.filter(id => id !== hubId);
            }
            return { ...pl, journeyConfigs };
        };
        setSublocations(prev => prev.filter(s => s.id !== hubId));
        setPlaylist(stripHub);
        setSavedPlaylist(stripHub);
    };

    // Deleting a hub deletes the sublocation record itself, so it disappears from every other
    // Journey that shows it too. Which playlists show a hub is decided by BIZ
    // (getSublocationsByPlaylistId — the same call Open uses), not by anything derivable from the
    // playlist list alone (location or journeyConfigs), so each other playlist is asked directly
    // before the confirm modal opens. If one of those lookups fails, that playlist falls back to
    // the location/journeyConfigs guess rather than being silently left out.
    const handleRequestDeleteHub = async (hub) => {
        if (checkingHubId) return;
        setCheckingHubId(hub.id);

        const referencesHub = (pl) =>
            pl.locationId === hub.locationId ||
            (pl.journeyConfigs?.sublocations || []).some(s => s.sublocationId === hub.id);
        const otherPlaylists = journeyPlaylists.filter(pl => pl.id !== playlist?.id && !pl.isDeleted);

        const uses = await Promise.all(otherPlaylists.map(pl =>
            getSublocationsByPlaylistId(pl.id)
                .then(subs => Array.isArray(subs) ? subs.some(s => s.id === hub.id) : referencesHub(pl))
                .catch(() => referencesHub(pl))
        ));

        setCheckingHubId(null);
        setDeletingHub({ hub, affectedPlaylists: otherPlaylists.filter((pl, i) => uses[i]) });
    };

    const handleEditHub = (sub) => {
        const apiSub = sublocations.find(s => s.id === sub.id) || sub;
        // name stays the sublocation's own (not the rule-resolved display name) — it seeds the
        // modal's Default label, which saves straight back onto the sublocation.
        setEditingSublocation({ ...apiSub, isActive: sub.isActive, badge: sub.badge });
    };

    // Writes the drop position into playlist.journeyConfigs.hubOrder — that's the combined
    // list's (hubs + dividers) only source of truth (journeyHubItems above derives display order
    // from it), so this is also what makes reordering hit Save/Undo/Revert the same as any other
    // journeyConfigs edit does.
    const handleHubDragStart = ({ active }) => {
        const heights = {};
        hubsTableRef.current?.querySelectorAll('[data-hub-item-id]').forEach(node => {
            heights[node.dataset.hubItemId] = node.offsetHeight;
        });
        setHubDrag({ activeId: active.id, overId: active.id, heights });
    };

    // Dragged outside the list, a drop does nothing (see handleHubDragEnd), so the rows go back
    // to where they started.
    const handleHubDragOver = ({ active, over }) => {
        setHubDrag(prev => prev && { ...prev, overId: over ? over.id : active.id });
    };

    const handleHubDragCancel = () => setHubDrag(null);

    const handleHubDragEnd = (event) => {
        setHubDrag(null);
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = journeyHubItems.findIndex(item => item.id === active.id);
        const newIndex = journeyHubItems.findIndex(item => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reorderedIds = arrayMove(journeyHubItems, oldIndex, newIndex).map(item => item.id);
        setPlaylist(prev => ({
            ...prev,
            journeyConfigs: { ...(prev?.journeyConfigs || {}), hubOrder: reorderedIds },
        }));
        setStateChanged(true);
    };

    const handleConfirmDivider = () => {
        const label = draftDivider?.label.trim();
        if (!label) return;
        const { id } = draftDivider;
        setPlaylist(prev => {
            const prevDividers = prev?.journeyConfigs?.dividers || {};
            const prevOrder = prev?.journeyConfigs?.hubOrder || [];
            // A brand new divider (no existing entry) is inserted at the top, matching where its
            // draft row appears; editing an existing one just updates its label in place.
            const nextOrder = prevDividers[id] ? prevOrder : [id, ...prevOrder];
            return {
                ...prev,
                journeyConfigs: {
                    ...(prev?.journeyConfigs || {}),
                    dividers: { ...prevDividers, [id]: { id, label } },
                    hubOrder: nextOrder,
                },
            };
        });
        setStateChanged(true);
        setDraftDivider(null);
    };

    const handleCancelDivider = () => setDraftDivider(null);

    const handleDeleteDivider = (id) => {
        setPlaylist(prev => {
            const prevDividers = { ...(prev?.journeyConfigs?.dividers || {}) };
            delete prevDividers[id];
            const prevOrder = (prev?.journeyConfigs?.hubOrder || []).filter(itemId => itemId !== id);
            return {
                ...prev,
                journeyConfigs: { ...(prev?.journeyConfigs || {}), dividers: prevDividers, hubOrder: prevOrder },
            };
        });
        setStateChanged(true);
        if (draftDivider?.id === id) setDraftDivider(null);
    };

    const handleSave = async () => {
        const resp = await savePlaylist({ playlist }, customer);
        if (resp?.status === 200) {
            setStatusMessage({ message: 'Your updates have been saved.', error: false });
            setStateChanged(false);
            const data = await getBasePlaylistsWrapper();
            // A newly-created playlist has no id locally yet — match by label+customerId to
            // pick up the id BIZ assigned, otherwise the next Save creates a duplicate instead
            // of updating.
            const createdPlaylist = (playlist?.id
                ? data.find(pl => pl.id === playlist.id)
                : data.find(pl => pl.label === playlist.label && pl.customerId === playlist.customerId)
            ) || playlist;
            setPlaylist(createdPlaylist);
            setSavedPlaylist(createdPlaylist);
        } else {
            setStatusMessage({ message: 'Something went wrong. Please try again.', error: true });
        }
    };

    const undoChange = () => {
        setPlaylist(savedPlaylist);
        setStateChanged(false);
    };

    const isEditable = useMemo(() => {
        let retval = false;
        if ((playlist?.id && playlist?.location?.id in permissions['journey'] && permissions['journey'][playlist.location.id].includes('editor')) ||
            playlist?._new) {
            retval = true;
        }

        return retval;
    }, [playlist]);

    const fillSvgColor = (svg, stroke, fill) => {
        if (stroke || fill) {
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', stroke);
                svg.children[i].setAttribute('fill', fill);
            }
        }
    }

    const userHasAccess = (feature) => {
        const defaultAllowed = ['basicConfiguration'].includes(feature.key);
        let journeyAllowed = 'journey' in permissions && playlist?.location?.id in permissions['journey'];
        let viewableFeature = feature.key in permissions && (playlist?.location?.id in permissions[feature.key]);

        return defaultAllowed || (viewableFeature && journeyAllowed);
    }

    const modifyIconColor = (svg, feature) => {
        const needsFill = ['interactivityIcon', 'insightIcon', 'paintbrushIcon', 'phoneTouchIcon'];
        if (!userHasAccess(feature) || !feature.enabled || !playlist?.createdAt) {
            // Gray if no access
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#CCD2D3');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', '#CCD2D3');
                }
            }
        } else if (selectedTab == feature.key) {
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', 'white');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', 'white');
                }
            }
        } else if (userHasAccess(feature) && feature.enabled && playlist?.createdAt) {
            // Explicitly turn it the "active" color
            for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', '#373739');
                if (needsFill.includes(feature.icon)) {
                    svg.children[i].setAttribute('fill', '#373739');
                }
            }
        }
    }

    const getMenuItem = (feature) => {
        if (feature.endGrouping) {
            menu.push(<div className="tab-separator" key={feature.key}></div>);
            return;
        } else {
            return <div key={feature.key} title={feature.title}>
                    <div
                        className={`icon-container ${!userHasAccess(feature) || (!playlist?.id && !playlist?._new) || !feature.enabled ? 'disabled' : (selectedTab === feature.key && (playlist?.id || playlist?._new) ? 'active' : '')}`}
                        key={feature.key + '-reactsvg'}
                        onClick={() => setSelectedTab(feature.key)}
                        title={feature.title}
                    >
                        <ReactSVG className="icon feature-icon" src={icons[feature.icon]} beforeInjection={(svg) => modifyIconColor(svg, feature)}/>
                    </div>
                </div>
        }
    }

    let menu = [];
    journeyPlaylistFeatures.forEach((feature) => {
        menu.push(getMenuItem(feature));
    })

    // A playlist is active by default — it only reads as inactive once its deactivationDate has
    // passed. Switching it off stamps deactivationDate with the current moment so it goes
    // inactive right away; switching it back on clears the date. Both are staged for Save.
    const isDistributionActive = !playlist?.deactivationDate || new Date(playlist.deactivationDate) > new Date();
    const handleDistributionActiveChange = (checked) => {
        handlePlaylistChange('deactivationDate', checked ? null : new Date().toISOString());
    };

    const distributionUrl = `${env.LOADER_URL}/${playlist?.shortCode || ''}`;
    const locationOptions = locations.map(l => ({ label: l.name, value: l.id }));

    return (
        <div className="JourneyPlaylistPage">
            {statusMessage.message && (
                <StatusModal setStatusMessage={setStatusMessage} statusMessage={statusMessage} />
            )}
            {showOpenPlaylistModal && (
                <OpenPlaylistModal
                    playlists={journeyPlaylists}
                    selectedPlaylist={playlist}
                    onOpen={async pl => {
                        const apiSubs = await getSublocationsByPlaylistId(pl.id);
                        // Display order is derived from journeyConfigs.hubOrder at render
                        // time (see subsWithBadge), once setPlaylist(pl) below is in effect.
                        if (Array.isArray(apiSubs)) setSublocations(apiSubs);

                        openedLocationIdRef.current = pl.locationId;
                        setPlaylist(pl);
                        setSavedPlaylist(pl);
                        setStateChanged(false);
                        setShowOpenPlaylistModal(false);
                    }}
                    onClose={() => setShowOpenPlaylistModal(false)}
                />
            )}
            {showNewPlaylistModal && (
                <NewPlaylistModal
                    locations={locations}
                    customer={customer}
                    onCreate={newPlaylist => {
                        setPlaylist(newPlaylist);
                        setSavedPlaylist(newPlaylist);
                        setStateChanged(true);
                        setShowNewPlaylistModal(false);
                    }}
                    onClose={() => setShowNewPlaylistModal(false)}
                />
            )}
            {showPreviewModal && (
                <PreviewModal state={{ playlist }} setShowPreviewModal={setShowPreviewModal} />
            )}
            {showUndoConfirmModal && (
                <UndoConfirmModal
                    onConfirm={() => { undoChange(); setShowUndoConfirmModal(false); }}
                    onClose={() => setShowUndoConfirmModal(false)}
                />
            )}
            {editingSublocation && (
                <SublocationEditModal
                    sublocation={editingSublocation}
                    rules={editingSublocation.rules || []}
                    playlistId={playlist?.id}
                    onSaveDetails={handleSublocationSave}
                    onSaveRules={handleRulesSave}
                    onClose={() => setEditingSublocation(null)}
                />
            )}
            {showCreateHubModal && (
                <CreateHubModal
                    locationId={playlist?.locationId}
                    onCreated={handleHubCreated}
                    onClose={() => setShowCreateHubModal(false)}
                />
            )}
            {deletingHub && (
                <DeleteHubConfirmModal
                    affectedPlaylists={deletingHub.affectedPlaylists}
                    onConfirm={async () => { await handleDeleteHub(deletingHub.hub.id); setDeletingHub(null); }}
                    onClose={() => setDeletingHub(null)}
                />
            )}

            <div className="actions-tab">
                <div className="left-container">
                    <button className="action-btn" onClick={() => setShowNewPlaylistModal(true)}>
                        <img src={plusIcon} alt="add" className="tab-icon" />
                    </button>
                    <button className="action-btn" onClick={() => setShowOpenPlaylistModal(true)}>
                        <img src={openReportIcon} alt="open" className="tab-icon" />
                    </button>
                    {playlist && (
                        <div className="playlist-info">
                            <div className="playlist-name">{playlist.label}</div>
                            {playlist.location && (
                                <div className="playlist-address">
                                    {[playlist.location.address1, playlist.location.city, playlist.location.state].filter(Boolean).join(', ')}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="right-container">
                    {menu}
                    <div className="tab-separator" />
                    <div title="Preview">
                        <div
                            className={`icon-container${!playlist || stateChanged ? ' disabled' : ''}`}
                            title="Preview"
                            onClick={() => playlist && !stateChanged && setShowPreviewModal(true)}
                        >
                            <ReactSVG
                                alt="preview"
                                beforeInjection={(svg) => !playlist?.id || stateChanged ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}
                                className="icon feature-icon"
                                src={icons['eyeIcon']}
                            />
                        </div>
                    </div>
                    <div title="Undo">
                        <div
                            className={`icon-container${!playlist || !isEditable ? ' disabled' : ''}`}
                            title="Undo"
                            onClick={() => isEditable ? playlist && stateChanged && setShowUndoConfirmModal(true) : null}
                        >
                            <ReactSVG
                                alt="undo"
                                beforeInjection={(svg) => !playlist?.id || !isEditable ? fillSvgColor(svg, '#CCD2D3', '#ffffff') : null}
                                className="icon feature-icon"
                                src={icons['returnIcon']}
                            />
                        </div>
                    </div>
                    <div title="Save">
                        <div
                            className={`icon-container save-btn${!playlist || !isEditable ? ' disabled' : (stateChanged ? ' changed' : '')}`}
                            title="Save"
                            onClick={() => isEditable ? playlist && handleSave() : null}
                        >
                            <ReactSVG
                                alt="save"
                                beforeInjection={(svg) => !playlist?.id || !isEditable ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}
                                className={`icon feature-icon ${stateChanged ? 'invert-color' : ''}`}
                                src={icons['saveIcon']}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedTab === 'journeyBrandingConfiguration' ? (
                <BrandingConfiguration
                    playlist={playlist}
                    onChange={handleBrandingChange}
                    handleContentModalOpen={handleContentModalOpen}
                    permissions={permissions}
                />
            ) : selectedTab === 'journeyPlaylistConfiguration' ? (
                <JourneyPlaylistConfiguration
                    playlist={playlist}
                    onChange={(field, value) => { handlePlaylistChange(field, value); }}
                    customer={customer}
                    permissions={permissions}
                />
            ) : selectedTab === 'journeyInsight' ? (
                <JourneyInsight
                    playlist={playlist}
                />
            ) : (
            <div className="journey-content">
                <div className="left-panel">
                  <div className="config-section">
                    <section className="config-subsection">
                        <h2>Distribution</h2>
                        <div className="distribution-row">
                            <span className="dist-label">Active:</span>
                            <Switch
                                checked={isDistributionActive}
                                onChange={handleDistributionActiveChange}
                                onColor="#2386EE"
                                offColor="#CCD2D3"
                                height={22}
                                width={44}
                                uncheckedIcon={false}
                                checkedIcon={false}
                                disabled={!playlist || !isEditable}
                            />
                            <input
                                className="url-input disabled"
                                type="text"
                                readOnly
                                value={distributionUrl}
                                disabled
                            />
                        </div>
                    </section>

                    <section className="config-subsection">
                        <h2>Localization settings</h2>
                        <p className="section-description">
                            Ensure your content is formatted properly by choosing the primary device(s) where you plan to display it.
                        </p>
                        <div className="form-row">
                            <span className="form-label">Name:</span>
                            <input
                                className="form-input"
                                type="text"
                                value={playlist?.label || ''}
                                onChange={e => handlePlaylistChange('label', e.target.value)}
                                disabled={!isEditable}
                            />
                        </div>
                        <div className="form-row">
                            <span className="form-label">Location:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={locationOptions}
                                value={locationOptions.find(o => o.value === playlist?.locationId) || null}
                                onChange={opt => handlePlaylistChange('locationId', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        {isAfAdmin(userData) && (
                            <div className="form-row">
                                <span className="form-label">Classification:</span>
                                <Select
                                    className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                    options={CLASSIFICATION_OPTIONS}
                                    value={CLASSIFICATION_OPTIONS.find(o => o.value === playlist?.usageType) || null}
                                    onChange={opt => handlePlaylistChange('usageType', opt.value)}
                                    styles={selectStyles}
                                    isDisabled={!isEditable}
                                />
                            </div>
                        )}
                        <div className="form-row">
                            <span className="form-label">Language:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={dropdownOptions.LANGUAGE_OPTIONS}
                                value={
                                    dropdownOptions.LANGUAGE_OPTIONS.find(o => o.value === playlist?.language)
                                    || dropdownOptions.LANGUAGE_OPTIONS[0]
                                }
                                onChange={opt => handlePlaylistChange('language', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        <div className="form-row">
                            <span className="form-label">Measurements:</span>
                            <Select
                                className={`form-select ${!playlist?.id ? 'disabled' : ''}`}
                                options={dropdownOptions.MEASUREMENTS}
                                value={
                                    dropdownOptions.MEASUREMENTS.find(o => o.value === playlist?.measurement)
                                    || dropdownOptions.MEASUREMENTS[0]
                                }
                                onChange={opt => handlePlaylistChange('measurement', opt.value)}
                                styles={selectStyles}
                                isDisabled={!isEditable}
                            />
                        </div>
                        <p className="support-note">
                            Need to add, remove, or update a location? Contact{' '}
                            <a href="mailto:support@actionfigure.ai">support@actionfigure.ai</a>
                        </p>
                    </section>

                    <section className="config-subsection">
                        <h2>Journey Hubs</h2>
                        <p className="section-description">
                            Journey Hubs are how you control where and how people get to the final destination. These could be parking lots, transit stations, and more.
                        </p>
                        <div className="hubs-table" ref={hubsTableRef}>
                            <div className="hubs-header">
                                <div className="header-labels">
                                    <span className="col-hub">Hub</span>
                                    <span className="col-active">Active</span>
                                    <span className="col-label">Label</span>
                                </div>
                                <div className="header-actions">
                                    <button
                                        type="button"
                                        className="hub-header-action"
                                        disabled={!playlist}
                                        onClick={() => setShowCreateHubModal(true)}
                                    >
                                        + Create a Hub
                                    </button>
                                    <button
                                        type="button"
                                        className="hub-header-action"
                                        disabled={!playlist || !!draftDivider}
                                        onClick={() => setDraftDivider({
                                            id: `divider-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                                            label: '',
                                        })}
                                    >
                                        + Divider
                                    </button>
                                </div>
                            </div>

                            {/* A brand new divider has no saved position yet, so it renders here
                                instead of in the ordered list below — confirming it is what adds
                                it to journeyConfigs.hubOrder (see handleConfirmDivider). */}
                            {draftDivider && !dividers[draftDivider.id] && (
                                <div className="hub-row hub-row--divider">
                                    <input
                                        type="text"
                                        className="divider-label-input"
                                        placeholder="Enter a label for this divider"
                                        value={draftDivider.label}
                                        onChange={e => setDraftDivider(prev => ({ ...prev, label: e.target.value }))}
                                        autoFocus
                                    />
                                    <div className="hub-row-actions">
                                        <button
                                            type="button"
                                            className="hub-action-btn hub-action-btn--confirm"
                                            title="Save divider"
                                            disabled={!draftDivider.label.trim()}
                                            onClick={handleConfirmDivider}
                                        >
                                            <ReactSVG className="icon" src={checkIcon} />
                                        </button>
                                        <button
                                            type="button"
                                            className="hub-action-btn hub-action-btn--cancel"
                                            title="Cancel"
                                            onClick={handleCancelDivider}
                                        >
                                            <ReactSVG className="icon icon--trash" src={trashIcon} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <DndContext
                                sensors={hubSensors}
                                collisionDetection={closestCenter}
                                modifiers={[restrictToVerticalAxis]}
                                onDragStart={handleHubDragStart}
                                onDragOver={handleHubDragOver}
                                onDragEnd={handleHubDragEnd}
                                onDragCancel={handleHubDragCancel}
                            >
                                {journeyHubItems.map(item => item.kind === 'divider' ? (
                                    <DividerRow
                                        key={item.id}
                                        shiftY={hubDragShifts[item.id] || 0}
                                        isSorting={!!hubDrag}
                                        divider={item.divider}
                                        isEditing={draftDivider?.id === item.id}
                                        draftLabel={draftDivider?.label ?? ''}
                                        onDraftLabelChange={label => setDraftDivider(prev => ({ ...prev, label }))}
                                        onConfirm={handleConfirmDivider}
                                        onCancelEdit={handleCancelDivider}
                                        onStartEdit={() => setDraftDivider({ id: item.id, label: item.divider.label })}
                                        onDelete={() => handleDeleteDivider(item.id)}
                                    />
                                ) : (
                                    <HubRow
                                        key={item.id}
                                        shiftY={hubDragShifts[item.id] || 0}
                                        isSorting={!!hubDrag}
                                        sub={item.hub}
                                        onEdit={handleEditHub}
                                        onDelete={canDeleteHubs ? handleRequestDeleteHub : null}
                                        isCheckingDelete={checkingHubId === item.id}
                                        deleteDisabled={!!checkingHubId}
                                    />
                                ))}
                                <DragOverlay>
                                    {draggedHubItem ? <HubDragPreview item={draggedHubItem} /> : null}
                                </DragOverlay>
                            </DndContext>
                        </div>
                        <p className="support-note">
                            Need to add or remove a Hub? Contact{' '}
                            <a href="mailto:support@actionfigure.ai">support@actionfigure.ai</a>
                        </p>
                    </section>
                  </div>
                </div>

                <div className="right-panel">
                    {mapLoaded ? (
                        <GoogleMap
                            mapContainerClassName="journey-map"
                            center={mapCenter}
                            zoom={13}
                            options={{ disableDefaultUI: true, zoomControl: true }}
                        >
                            {subsWithBadge.map(sub => {
                                const lat = parseFloat(sub.latitude);
                                const lng = parseFloat(sub.longitude);
                                return isFinite(lat) && isFinite(lng) ? (
                                    <OverlayView
                                        key={sub.id}
                                        position={{ lat, lng }}
                                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                                        getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
                                    >
                                        <div className={`hub-map-marker${sub.isFinal ? ' final' : ''}`}>
                                            {sub.isFinal ? (
                                                <ReactSVG className="hub-flag-icon" src={flagIcon} />
                                            ) : sub.badge}
                                        </div>
                                    </OverlayView>
                                ) : null;
                            })}
                        </GoogleMap>
                    ) : (
                        <div className="map-loading">Loading map...</div>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

const OpenPlaylistModal = ({ playlists, selectedPlaylist, onOpen, onClose }) => {
    const [highlighted, setHighlighted] = useState(selectedPlaylist);
    const [sortColumn, setSortColumn] = useState('updatedAt');
    const [sortDirection, setSortDirection] = useState('desc');

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const handleSort = (col) => {
        if (sortColumn === col) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(col);
            setSortDirection('asc');
        }
    };

    const sorted = [...playlists].sort((a, b) => {
        let aVal = sortColumn === 'locationId' ? (a.location?.name || '') : (a[sortColumn] ?? '');
        let bVal = sortColumn === 'locationId' ? (b.location?.name || '') : (b[sortColumn] ?? '');
        if (['updatedAt', 'createdAt'].includes(sortColumn)) {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        }
        const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : (aVal > bVal ? 1 : aVal < bVal ? -1 : 0);
        return sortDirection === 'asc' ? cmp : -cmp;
    });

    return (
        <div className="modal-wrapper">
            <div className="OpenPlaylistModal modal">
                <div className="modal-header">
                    <h2>Open a Journey Configuration</h2>
                </div>
                <div className="table-container">
                    <table className="playlists-table">
                        <tbody>
                            <tr className="header-row">
                                <th className="table-header" onClick={() => handleSort('shortCode')}>Shortcode</th>
                                <th className="table-header" onClick={() => handleSort('label')}>Name</th>
                                <th className="table-header" onClick={() => handleSort('locationId')}>Location</th>
                                <th className="table-header" onClick={() => handleSort('updatedBy')}>Updated by</th>
                                <th className="table-header" onClick={() => handleSort('updatedAt')}>Updated</th>
                            </tr>
                            {sorted
                                .filter(pl => !pl.isDeleted)
                                .map(pl => (
                                <tr
                                    key={pl.id}
                                    className={`playlist-row${pl.id === highlighted?.id ? ' selected' : ''}`}
                                    onClick={() => setHighlighted(pl)}
                                >
                                    <td>{pl.shortCode}</td>
                                    <td>{pl.label}</td>
                                    <td>{pl.location?.name || '—'}</td>
                                    <td>{pl.updatedBy || '—'}</td>
                                    <td>{formatDate(pl.updatedAt)}</td>
                                </tr>
                            ))}
                            {playlists.length === 0 && (
                                <tr><td colSpan={3} className="empty-row">No journey playlists found.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button
                        className="button modal-button"
                        disabled={!highlighted}
                        onClick={() => highlighted && onOpen(highlighted)}
                    >
                        Open
                    </button>
                </div>
            </div>
        </div>
    );
};

const NewPlaylistModal = ({ locations, customer, onCreate, onClose }) => {
    const [name, setName] = useState('');
    const [locationId, setLocationId] = useState(null);

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const locationOptions = locations.map(l => ({ label: l.name, value: l.id }));

    const handleCreate = () => {
        onCreate({
            customerId: customer.id,
            locationId,
            label: name,
            language: 'en',
            screenType: 'journey',
            deviceType: 'landscape',
            access: 'public',
            type: 'base',
            createdAt: new Date(),
            _new: true,
        });
    };

    return (
        <div className="modal-wrapper">
            <div className="NewPlaylistModal modal">
                <div className="modal-header">
                    <h2>Create a new Journey</h2>
                </div>
                <div className="modal-form">
                    <div className="modal-form-row">
                        <label className="modal-form-label">Name:</label>
                        <input
                            className="modal-form-input"
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="ex: Fair New York World’s Fair"
                        />
                    </div>
                    <div className="modal-form-row">
                        <label className="modal-form-label">Location:</label>
                        <Select
                            className="modal-form-select"
                            options={locationOptions}
                            value={locationOptions.find(o => o.value === locationId) || null}
                            onChange={opt => setLocationId(opt.value)}
                            styles={selectStyles}
                            placeholder="Select a location..."
                        />
                    </div>
                </div>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button
                        className="button modal-button"
                        disabled={!name || !locationId}
                        onClick={handleCreate}
                    >
                        Create
                    </button>
                </div>
            </div>
        </div>
    );
};

const StatusModal = ({ setStatusMessage, statusMessage }) => {
    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    return (
        <div className="modal-wrapper">
            <div className="StatusModal modal">
                <p className={statusMessage.error ? 'error' : ''}>{statusMessage.message}</p>
                <div className="bottom-container">
                    <button
                        className="button modal-button"
                        onClick={() => setStatusMessage({ message: null, error: false })}
                    >
                        Ok
                    </button>
                </div>
            </div>
        </div>
    );
};

const UndoConfirmModal = ({ onConfirm, onClose }) => {
    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    return (
        <div className="modal-wrapper">
            <div className="StatusModal modal">
                <p>This will remove all changes since the last time you saved. Are you sure?</p>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose}>Cancel</button>
                    <button className="button modal-button" onClick={onConfirm}>Ok</button>
                </div>
            </div>
        </div>
    );
};

// affectedPlaylists is worked out before this opens (see handleRequestDeleteHub).
const DeleteHubConfirmModal = ({ affectedPlaylists, onConfirm, onClose }) => {
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    return (
        // Clicking the backdrop (but not the modal itself) acts like Cancel — except mid-delete.
        <div className="modal-wrapper" onClick={e => e.target === e.currentTarget && !deleting && onClose()}>
            <div className="DeleteHubConfirmModal modal">
                <ReactSVG className="delete-hub-icon" src={trashIcon} />
                <div className="delete-hub-message">
                    <h2>Delete</h2>
                    <p>This action cannot be undone. Analytics collected for this Hub will not be deleted. Are you sure?</p>
                    {affectedPlaylists.length > 0 ? (
                        <div className="delete-hub-affected">
                            <p className="delete-hub-affected-title">
                                This Hub will also be removed from {affectedPlaylists.length === 1 ? 'this Journey' : `these ${affectedPlaylists.length} Journeys`}:
                            </p>
                            <ul>
                                {affectedPlaylists.map(pl => (
                                    <li key={pl.id}>
                                        {pl.label}
                                        {pl.shortCode && <span className="delete-hub-affected-code"> ({pl.shortCode})</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <p className="delete-hub-affected-none">No other Journeys use this Hub.</p>
                    )}
                </div>
                <div className="delete-hub-actions">
                    <button
                        className="button delete-button"
                        disabled={deleting}
                        onClick={() => { setDeleting(true); onConfirm(); }}
                    >
                        {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                    <button className="button cancel-button" onClick={onClose} disabled={deleting}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

const EMPTY_HUB = {
    name: '',
    address: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    latitude: '',
    longitude: '',
    timezone: '',
};

const CreateHubModal = ({ locationId, onCreated, onClose }) => {
    const [hub, setHub] = useState({ ...EMPTY_HUB });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        document.body.classList.add('no-scroll');
        return () => document.body.classList.remove('no-scroll');
    }, []);

    const handleFieldChange = (field, value) => {
        setHub(prev => ({ ...prev, [field]: value }));
    };

    // AddressField is the only supported lookup method right now — this just records whatever
    // it resolves (address, city/state/zip, lat/lng, timezone) onto the hub being created.
    const handleAddressSelect = (loc) => {
        setHub(prev => ({
            ...prev,
            ...loc,
            latitude: loc?.latitude != null ? parseFloat(loc.latitude.toFixed(6)) : loc?.latitude,
            longitude: loc?.longitude != null ? parseFloat(loc.longitude.toFixed(6)) : loc?.longitude,
        }));
    };

    const canSubmit = !!hub.name.trim() && !!hub.address && !saving;

    const handleCreate = async () => {
        if (!canSubmit) return;
        setSaving(true);
        try {
            const created = await createSublocation({
                ...hub,
                locationId,
                isPreset: true,
                isFinal: true,
            });
            if (created?.id) {
                onCreated(created);
                onClose();
            }
        } catch (e) {
            console.error('Failed to create hub', e);
        }
        setSaving(false);
    };

    return (
        <div className="modal-wrapper">
            <div className="CreateHubModal modal">
                <div className="modal-header">
                    <h2>Create a Hub</h2>
                </div>
                <div className="form-row">
                    <label className="form-label">Label:</label>
                    <input
                        className="text-input"
                        value={hub.name}
                        onChange={e => handleFieldChange('name', e.target.value)}
                        placeholder="e.g. Lot G7"
                    />
                </div>
                <div className="form-row">
                    <label className="form-label">Address:</label>
                    <input type="radio" checked onChange={() => {}} />
                    <AddressField
                        existingLocation={hub}
                        placeholder="Search for an address…"
                        searchValue={hub.address}
                        editLocation={handleAddressSelect}
                        setIsLocationEdited={() => {}}
                    />
                </div>
                <div className="form-row">
                    {/* Coordinate lookup isn't supported yet — address search is the only way in for now. */}
                    <label className="form-label">Coordinates:</label>
                    <input type="radio" checked={false} disabled onChange={() => {}} />
                    <input
                        className="text-input text-input--small"
                        value={hub.latitude}
                        placeholder="-26.872434543"
                        disabled
                        readOnly
                    />
                    <span className="coordinate-separator">,</span>
                    <input
                        className="text-input text-input--small"
                        value={hub.longitude}
                        placeholder="4.92874529"
                        disabled
                        readOnly
                    />
                </div>
                <div className="bottom-container">
                    <button className="button cancel-button" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="button modal-button" onClick={handleCreate} disabled={!canSubmit}>
                        {saving ? 'Creating…' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// The pointer moves the copy in DragOverlay, not the rows themselves: a row only ever slides up or
// down by the shift JourneyPlaylistPage works out (hubDragShifts). The transition runs only while
// a drag is in progress, so on drop the rows snap straight into their new DOM order instead of
// animating away from it.
const sortStyle = (shiftY, isSorting) => ({
    transform: shiftY ? `translate3d(0, ${shiftY}px, 0)` : undefined,
    transition: isSorting ? 'transform 200ms ease' : undefined,
});

// Hubs only reorder up and down, so the dragged copy shouldn't wander sideways out of the list.
const restrictToVerticalAxis = ({ transform }) => ({ ...transform, x: 0 });

const HubColumns = ({ sub }) => (
    <>
        <div className="col-hub">
            <div className={`hub-badge${sub.isFinal ? ' final' : ''}`}>
                {sub.isFinal ? (
                    <ReactSVG className="hub-flag-icon" src={flagIcon} />
                ) : sub.badge}
            </div>
        </div>
        <div className="col-active">
            <span className={`active-indicator${sub.isActive ? ' active' : ''}`}>
                {sub.isActive ? 'Active' : 'Inactive'}
            </span>
        </div>
        <div className="col-label">{sub.name}</div>
    </>
);

const DividerColumns = ({ divider }) => (
    <>
        <div className="col-hub" />
        <div className="col-active" />
        <div className="col-label">{divider.label}</div>
    </>
);

// The lifted copy that follows the pointer while a row is dragged; the row itself stays in the
// list as the placeholder showing where it will land. Only the handle is shown on the right, since
// that's what is being held and the copy's other buttons couldn't be clicked anyway.
const HubDragPreview = ({ item }) => (
    <div className={`hub-row hub-row--overlay${item.kind === 'divider' ? ' hub-row--divider' : ''}`}>
        {item.kind === 'divider' ? <DividerColumns divider={item.divider} /> : <HubColumns sub={item.hub} />}
        <div className="hub-row-actions">
            <span className="hub-action-btn hub-drag-handle">
                <ReactSVG className="icon" src={dragHandleIcon} />
            </span>
        </div>
    </div>
);

// A hub row is both draggable (by its handle) and droppable (as a target other rows can be
// dragged onto) — same id for both, same idea as @dnd-kit/sortable's useSortable, which isn't
// installed here so this composes the two @dnd-kit/core hooks by hand.
const HubRow = ({ sub, shiftY, isSorting, onEdit, onDelete, isCheckingDelete, deleteDisabled }) => {
    const { attributes, listeners, setNodeRef: setDragNodeRef, isDragging } = useDraggable({ id: sub.id });
    const { setNodeRef: setDropNodeRef } = useDroppable({ id: sub.id });

    const setRefs = (node) => {
        setDragNodeRef(node);
        setDropNodeRef(node);
    };

    return (
        <div
            ref={setRefs}
            data-hub-item-id={sub.id}
            style={sortStyle(shiftY, isSorting)}
            className={`hub-row${isDragging ? ' hub-row--dragging' : ''}`}
        >
            <HubColumns sub={sub} />
            <div className="hub-row-actions">
                <button
                    type="button"
                    className="hub-action-btn"
                    title="Edit hub"
                    onClick={() => onEdit(sub)}
                >
                    <ReactSVG className="icon" src={editIcon} />
                </button>
                {onDelete && (
                    <button
                        type="button"
                        className={`hub-action-btn hub-action-btn--delete${isCheckingDelete ? ' hub-action-btn--busy' : ''}`}
                        title={isCheckingDelete ? 'Checking other Journeys…' : 'Delete hub'}
                        disabled={deleteDisabled}
                        onClick={() => onDelete(sub)}
                    >
                        <ReactSVG className="icon icon--trash" src={trashIcon} />
                    </button>
                )}
                <button
                    type="button"
                    className="hub-action-btn hub-drag-handle"
                    title="Drag to reorder"
                    {...attributes}
                    {...listeners}
                >
                    <ReactSVG className="icon" src={dragHandleIcon} />
                </button>
            </div>
        </div>
    );
};

// A divider has no sublocation record — it's purely a journeyConfigs.dividers entry with a slot
// in journeyConfigs.hubOrder — but otherwise participates in the same drag-and-drop as HubRow
// above (same id used for both its draggable and droppable registration).
const DividerRow = ({ divider, shiftY, isSorting, isEditing, draftLabel, onDraftLabelChange, onConfirm, onCancelEdit, onStartEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef: setDragNodeRef, isDragging } = useDraggable({ id: divider.id, disabled: isEditing });
    const { setNodeRef: setDropNodeRef } = useDroppable({ id: divider.id, disabled: isEditing });

    const setRefs = (node) => {
        setDragNodeRef(node);
        setDropNodeRef(node);
    };

    const style = sortStyle(shiftY, isSorting);

    // Editing keeps the wide single-input layout from the create flow; confirmed dividers render
    // like any other hub row (col-hub/col-active/col-label) so they read as part of the same
    // list, just with no badge and no active status.
    if (isEditing) {
        return (
            <div
                ref={setRefs}
                data-hub-item-id={divider.id}
                style={style}
                className={`hub-row hub-row--divider hub-row--divider-editing${isDragging ? ' hub-row--dragging' : ''}`}
            >
                <input
                    type="text"
                    className="divider-label-input"
                    placeholder="Enter a label for this divider"
                    value={draftLabel}
                    onChange={e => onDraftLabelChange(e.target.value)}
                    autoFocus
                />
                <div className="hub-row-actions">
                    <button
                        type="button"
                        className="hub-action-btn hub-action-btn--confirm"
                        title="Save divider"
                        disabled={!draftLabel.trim()}
                        onClick={onConfirm}
                    >
                        <ReactSVG className="icon" src={checkIcon} />
                    </button>
                    <button
                        type="button"
                        className="hub-action-btn hub-action-btn--cancel"
                        title="Cancel"
                        onClick={onCancelEdit}
                    >
                        <ReactSVG className="icon icon--trash" src={trashIcon} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={setRefs}
            data-hub-item-id={divider.id}
            style={style}
            className={`hub-row hub-row--divider${isDragging ? ' hub-row--dragging' : ''}`}
        >
            <DividerColumns divider={divider} />
            <div className="hub-row-actions">
                <button type="button" className="hub-action-btn" title="Edit divider" onClick={onStartEdit}>
                    <ReactSVG className="icon" src={editIcon} />
                </button>
                <button type="button" className="hub-action-btn" title="Delete divider" onClick={onDelete}>
                    <ReactSVG className="icon icon--trash" src={trashIcon} />
                </button>
                <button
                    type="button"
                    className="hub-action-btn hub-drag-handle"
                    title="Drag to reorder"
                    {...attributes}
                    {...listeners}
                >
                    <ReactSVG className="icon" src={dragHandleIcon} />
                </button>
            </div>
        </div>
    );
};

export default JourneyPlaylistPage;
